import { isTentativeDate } from './scheduleWeek';

/** The fields of a schedule event the Upcoming section shows. */
export interface UpcomingEvent {
  id: number;
  name: string;
  eventDate: string;
  start: string;
  end: string;
  displayTime?: string;
}

export interface UpcomingDay {
  date: string; // yyyy-MM-dd
  events: UpcomingEvent[];
}

export interface UpcomingWeek {
  /** Sunday that starts this Sun–Sat week (the loader's week), yyyy-MM-dd. */
  weekStart: string;
  /** After the current Sun–Sat week (America/Chicago): projected, may change. */
  tentative: boolean;
  days: UpcomingDay[];
  shiftCount: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Sunday on or before a yyyy-MM-dd date (calendar math in UTC, no time zone drift). */
export const sundayOf = (isoDate: string): string => {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d, 12));
  day.setUTCDate(day.getUTCDate() - day.getUTCDay());
  return `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}`;
};

/**
 * Group upcoming shifts into Sun–Sat weeks, then days (both ascending; shifts
 * within a day by start). Days on or before {@code shownThrough} — the last day
 * of the "This week" section, which the backend computes Mon–Sun — are dropped
 * so nothing appears twice. Days with no shifts are omitted.
 */
export const groupUpcoming = (
  events: UpcomingEvent[],
  shownThrough: string,
  now: Date = new Date()
): UpcomingWeek[] => {
  const byDate = new Map<string, UpcomingEvent[]>();
  for (const e of events) {
    const date = e.eventDate?.slice(0, 10);
    if (!date || date <= shownThrough) {
      continue;
    }
    const list = byDate.get(date) ?? [];
    list.push(e);
    byDate.set(date, list);
  }

  const weeks = new Map<string, UpcomingWeek>();
  for (const date of [...byDate.keys()].sort()) {
    const dayEvents = byDate
      .get(date)!
      .slice()
      .sort((a, b) => a.start.localeCompare(b.start));
    const weekStart = sundayOf(date);
    let week = weeks.get(weekStart);
    if (!week) {
      week = { weekStart, tentative: isTentativeDate(date, now), days: [], shiftCount: 0 };
      weeks.set(weekStart, week);
    }
    week.days.push({ date, events: dayEvents });
    week.shiftCount += dayEvents.length;
  }
  return [...weeks.values()];
};

/** A requested (exception) shift as `/events/pending` returns it. */
export interface RequestedShift {
  eventId: number;
  name: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  displayTime?: string;
  durationMinutes?: number;
}

/**
 * Add requested time to a list of approved shifts so staff see it as part of
 * their schedule (approval is a manager formality, never shown to staff). The
 * schedule endpoints return approved shifts only; an id already present wins.
 */
export const withRequestedShifts = <T extends UpcomingEvent>(
  events: T[],
  requested: RequestedShift[]
): Array<T | UpcomingEvent> => {
  const ids = new Set(events.map((e) => e.id));
  const extra: UpcomingEvent[] = requested
    .filter((r) => !ids.has(r.eventId))
    .map((r) => ({
      id: r.eventId,
      name: r.name,
      eventDate: r.eventDate,
      start: r.startTime,
      end: r.endTime,
      displayTime: r.displayTime,
    }));
  return [...events, ...extra];
};
